import { cache } from 'react';

import { ISsrDebugEntry } from './ssrDebugTypes';

export interface ISsrDebugCollector {
    init: () => Promise<void>;
    log: (label: string, data?: unknown) => Promise<void>;
    getEntries: () => ISsrDebugEntry[];
}

/**
 * Builds a request-scoped SSR debug entry collector on top of React's
 * `cache()`, so every call within the same request shares the same
 * memoized entries array and enabled-check. Call once per app at module
 * load and reuse the returned singleton everywhere.
 *
 * @example
 * const collector = createSsrDebugCollector(async () => {
 *   const cookieStore = await cookies();
 *   return cookieStore.get('my-app-ssr-debug')?.value === 'true';
 * });
 * export const { init: initSsrDebug, log: ssrDebugLog, getEntries: getSsrDebugEntries } = collector;
 */
export const createSsrDebugCollector = (getIsEnabled: () => Promise<boolean>): ISsrDebugCollector => {
    const getEntries = cache((): ISsrDebugEntry[] => []);
    const isEnabled = cache(getIsEnabled);

    return {
        init: async (): Promise<void> => {
            await isEnabled();
        },
        log: async (label: string, data?: unknown): Promise<void> => {
            if (!(await isEnabled())) return;
            getEntries().push({ timestamp: Date.now(), label, data });
        },
        getEntries: (): ISsrDebugEntry[] => getEntries(),
    };
};
